import React, { useEffect } from "react";
import Header from "../components/Header";
import { motion } from "framer-motion";
import HowItWorks from "../components/HowItWorks";
import Footer from "../components/Footer";
import { useNavigate } from "react-router-dom";
import { gerarLinkWhatsApp } from "../../utils/WhatsappLink";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CheckIcon from "@mui/icons-material/Check";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import LaptopMacOutlinedIcon from "@mui/icons-material/LaptopMacOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";

// Padrão visual compartilhado com as seções da home
const eyebrowSx = (dark) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    px: 1.75,
    py: 0.75,
    borderRadius: "999px",
    backgroundColor: dark ? "rgba(255, 200, 69, 0.12)" : "rgba(255, 200, 69, 0.25)",
    border: dark ? "1px solid rgba(255, 200, 69, 0.4)" : "none",
    color: dark ? "#ffc845" : "#0b243d",
    fontSize: "0.8rem",
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
});

const Eyebrow = ({ dark, children }) => (
    <Box sx={{ ...eyebrowSx(dark), mb: 2.5 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#ffc845" }} />
        {children}
    </Box>
);

const primaryButtonSx = {
    px: 4,
    py: 1.75,
    fontSize: "1rem",
    fontWeight: 700,
    textTransform: "none",
    backgroundColor: "#ffc845",
    color: "#0b243d",
    borderRadius: "10px",
    boxShadow: "0 8px 24px rgba(255, 200, 69, 0.35)",
    transition: "all 0.2s ease",
    '&:hover': {
        backgroundColor: "#e9b53d",
        transform: "translateY(-2px)",
        boxShadow: "0 10px 28px rgba(255, 200, 69, 0.45)",
    },
};

const outlinedDarkButtonSx = {
    px: 4,
    py: 1.75,
    fontSize: "1rem",
    fontWeight: 600,
    textTransform: "none",
    color: "#fff",
    borderColor: "rgba(255, 255, 255, 0.4)",
    borderRadius: "10px",
    transition: "all 0.2s ease",
    '&:hover': {
        borderColor: "#fff",
        backgroundColor: "rgba(255, 255, 255, 0.08)",
        transform: "translateY(-2px)",
    },
};

const anelDecorativo = (posicao) => ({
    position: "absolute",
    ...posicao,
    width: { xs: 240, md: 380 },
    height: { xs: 240, md: 380 },
    borderRadius: "50%",
    border: { xs: "36px solid #ffc845", md: "56px solid #ffc845" },
    opacity: 0.1,
    pointerEvents: "none",
});

// O que guia o nosso trabalho (extraído da nossa missão e propósito)
const valores = [
    { icon: MedicalServicesOutlinedIcon, titulo: "Especialistas em saúde", texto: "Conhecemos a fundo a rotina, os impostos e as obrigações de quem atua na área da saúde." },
    { icon: VerifiedUserOutlinedIcon, titulo: "Ética", texto: "Transparência em cada orientação. Você sempre sabe o que está sendo feito e por quê." },
    { icon: LockOutlinedIcon, titulo: "Sigilo absoluto", texto: "Seus dados e os da sua empresa tratados com o máximo cuidado e segurança." },
    { icon: WorkspacePremiumOutlinedIcon, titulo: "Excelência técnica", texto: "Precisão em cada entrega, para que sua contabilidade esteja sempre em dia." },
    { icon: FavoriteBorderOutlinedIcon, titulo: "Atendimento humanizado", texto: "Escuta ativa, proximidade e linguagem simples. Aqui você fala com gente que entende você." },
    { icon: LaptopMacOutlinedIcon, titulo: "100% online", texto: "Tecnologia a seu favor: resolva tudo de onde estiver, sem burocracia." },
];

const AboutUs = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [])

    const falarConosco = () => window.open(gerarLinkWhatsApp("Olá! Conheci a Clarea e gostaria de conversar sobre a contabilidade da minha atividade na saúde."), "_blank");

    return (
        <>
            <Header active="sobreNos" />

            {/* Abertura */}
            <Box
                component="section"
                sx={{
                    position: "relative",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #0b243d 0%, #12365a 100%)",
                    color: "#fff",
                    pt: { xs: "120px", md: "140px" },
                    pb: { xs: 8, md: 10 },
                    overflow: "hidden",
                }}
            >
                <Box sx={anelDecorativo({ bottom: { xs: -100, md: -160 }, right: { xs: -100, md: -160 } })} />

                <Box
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        width: "90%",
                        maxWidth: "1280px",
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        alignItems: "center",
                        gap: { xs: 6, md: 8 },
                    }}
                >
                    {/* Texto */}
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <Eyebrow dark>Sobre nós</Eyebrow>
                        <h1 style={{ margin: "0 0 20px", fontSize: "clamp(2rem, 4.4vw, 3.2rem)", fontWeight: 800, lineHeight: 1.15, letterSpacing: "-0.5px" }}>
                            A contabilidade digital que cuida de{" "}
                            <span style={{ color: "#ffc845" }}>quem cuida das pessoas.</span>
                        </h1>
                        <Box component="p" sx={{ m: 0, fontSize: { xs: "1rem", md: "1.1rem" }, lineHeight: 1.7, color: "rgba(255, 255, 255, 0.82)" }}>
                            A Clarea Vital nasceu com um propósito claro: <b style={{ color: "#fff" }}>ser referência nacional em contabilidade e serviços fiscais especializados para profissionais da saúde.</b>
                        </Box>

                        <Box sx={{ mt: 4, display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
                            <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => navigate("../Planos")} sx={primaryButtonSx}>
                                Conheça nossos planos
                            </Button>
                            <Button variant="outlined" startIcon={<WhatsAppIcon />} onClick={falarConosco} sx={outlinedDarkButtonSx}>
                                Fale com a gente
                            </Button>
                        </Box>
                    </motion.div>

                    {/* Imagem */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <Box sx={{ position: "relative", maxWidth: { xs: "520px", md: "none" }, mx: "auto" }}>
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: { xs: -24, md: -36 },
                                    right: { xs: -24, md: -36 },
                                    width: { xs: 110, md: 160 },
                                    height: { xs: 110, md: 160 },
                                    borderRadius: "50%",
                                    border: { xs: "20px solid #ffc845", md: "28px solid #ffc845" },
                                    opacity: 0.35,
                                }}
                            />
                            <Box
                                component="img"
                                src="/assets/sobreNos.jpeg"
                                alt="Equipe Clarea Vital"
                                sx={{
                                    position: "relative",
                                    display: "block",
                                    width: "100%",
                                    height: "auto",
                                    borderRadius: "24px",
                                    objectFit: "cover",
                                    boxShadow: "0 24px 60px rgba(0, 0, 0, 0.4)",
                                }}
                            />
                        </Box>
                    </motion.div>
                </Box>
            </Box>

            {/* Nossa história */}
            <Box component="section" sx={{ width: "100%", display: "flex", justifyContent: "center", backgroundColor: "#fff", py: { xs: 8, md: 12 }, overflow: "hidden" }}>
                <Box
                    sx={{
                        width: "90%",
                        maxWidth: "1280px",
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "5fr 6fr" },
                        alignItems: "center",
                        gap: { xs: 6, md: 8 },
                    }}
                >
                    {/* Imagem */}
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true, amount: 0.2 }}
                    >
                        <Box sx={{ position: "relative", maxWidth: { xs: "520px", md: "none" }, mx: "auto" }}>
                            <Box
                                sx={{
                                    position: "absolute",
                                    left: { xs: -12, md: -20 },
                                    bottom: { xs: -12, md: -20 },
                                    width: "60%",
                                    height: "60%",
                                    borderRadius: "24px",
                                    backgroundColor: "#0b243d",
                                }}
                            />
                            <Box
                                component="img"
                                src="/assets/sobreNos2.jpeg"
                                alt="Profissional da saúde atendido pela Clarea"
                                sx={{
                                    position: "relative",
                                    display: "block",
                                    width: "100%",
                                    height: "auto",
                                    borderRadius: "24px",
                                    objectFit: "cover",
                                    boxShadow: "0 20px 50px rgba(11, 36, 61, 0.25)",
                                }}
                            />
                        </Box>
                    </motion.div>

                    {/* Texto */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.15 }}
                        viewport={{ once: true, amount: 0.2 }}
                    >
                        <Eyebrow>Quem somos</Eyebrow>
                        <h2 style={{ margin: "0 0 20px", fontSize: "clamp(1.8rem, 3.2vw, 2.5rem)", fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.5px", color: "#0b243d" }}>
                            Mais do que números, entregamos <span style={{ color: "#c8961e" }}>confiança.</span>
                        </h2>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, fontSize: { xs: "1rem", md: "1.05rem" }, lineHeight: 1.75, color: "#4b5563", "& b": { color: "#0b243d" } }}>
                            <Box component="p" sx={{ m: 0 }}>
                                Unimos tecnologia, precisão técnica e profundo conhecimento do setor para oferecer soluções contábeis seguras, claras e personalizadas. <b>Cada detalhe do nosso trabalho é pautado por ética, sigilo absoluto e dedicação total à sua tranquilidade.</b>
                            </Box>
                            <Box component="p" sx={{ m: 0 }}>
                                Estamos ao lado de médicos, psicólogos, nutricionistas, preparadores físicos, gestores de clínicas e tantos outros que buscam <b>crescer com solidez</b>, sabendo que sua contabilidade está em mãos verdadeiramente especializadas.
                            </Box>
                        </Box>

                        {/* Frase de impacto */}
                        <Box
                            sx={{
                                mt: 4,
                                position: "relative",
                                p: { xs: 2.5, md: 3 },
                                pl: { xs: 2.5, md: 3.5 },
                                borderRadius: "16px",
                                backgroundColor: "#f9f2e4",
                                borderLeft: "5px solid #ffc845",
                            }}
                        >
                            <FormatQuoteRoundedIcon sx={{ position: "absolute", top: 10, right: 14, fontSize: 44, color: "rgba(200, 150, 30, 0.25)" }} />
                            <Box sx={{ fontSize: { xs: "1.15rem", md: "1.3rem" }, fontWeight: 800, color: "#0b243d", lineHeight: 1.35 }}>
                                Na Clarea, excelência não é meta — é padrão.
                            </Box>
                        </Box>
                    </motion.div>
                </Box>
            </Box>

            {/* Missão */}
            <Box component="section" sx={{ width: "100%", display: "flex", justifyContent: "center", backgroundColor: "#f9f2e4", py: { xs: 8, md: 10 } }}>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    viewport={{ once: true, amount: 0.3 }}
                    style={{ width: "90%", maxWidth: "1280px" }}
                >
                    <Box
                        sx={{
                            position: "relative",
                            overflow: "hidden",
                            borderRadius: "24px",
                            background: "linear-gradient(135deg, #0b243d 0%, #12365a 100%)",
                            color: "#fff",
                            px: { xs: 3, md: 8 },
                            py: { xs: 5, md: 7 },
                            textAlign: "center",
                            boxShadow: "0 24px 60px rgba(11, 36, 61, 0.25)",
                        }}
                    >
                        <Box sx={anelDecorativo({ top: { xs: -120, md: -180 }, left: { xs: -120, md: -180 } })} />
                        <Box sx={{ position: "relative" }}>
                            <Eyebrow dark>Nossa missão</Eyebrow>
                            <Box
                                component="p"
                                sx={{
                                    m: "0 auto",
                                    maxWidth: "900px",
                                    fontSize: "clamp(1.25rem, 2.4vw, 1.8rem)",
                                    fontWeight: 700,
                                    lineHeight: 1.45,
                                    "& span": { color: "#ffc845" },
                                }}
                            >
                                Oferecer soluções contábeis e fiscais de alta qualidade, com <span>ética, sigilo e excelência</span>, promovendo organização, crescimento e tranquilidade aos profissionais da saúde por meio de um suporte personalizado e 100% online.
                            </Box>
                        </Box>
                    </Box>
                </motion.div>
            </Box>

            {/* Nossos valores */}
            <Box component="section" sx={{ width: "100%", display: "flex", justifyContent: "center", backgroundColor: "#fff", py: { xs: 8, md: 12 } }}>
                <Box sx={{ width: "90%", maxWidth: "1280px" }}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.6 }}
                    >
                        <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
                            <Eyebrow>Por que a Clarea</Eyebrow>
                            <h2 style={{ margin: "0 0 12px", fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)", fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.5px", color: "#0b243d" }}>
                                O que nos torna a <span style={{ color: "#c8961e" }}>escolha certa</span>
                            </h2>
                            <Box component="p" sx={{ m: "0 auto", maxWidth: "580px", fontSize: { xs: "1rem", md: "1.05rem" }, lineHeight: 1.7, color: "#4b5563" }}>
                                Os princípios que guiam cada entrega e fazem da sua contabilidade uma preocupação a menos.
                            </Box>
                        </Box>
                    </motion.div>

                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: { xs: 2, md: 2.5 } }}>
                        {valores.map((valor, index) => {
                            const Icon = valor.icon;
                            return (
                                <motion.div
                                    key={valor.titulo}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.45, delay: (index % 3) * 0.1 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    style={{ height: "100%" }}
                                >
                                    <Box
                                        sx={{
                                            height: "100%",
                                            boxSizing: "border-box",
                                            p: { xs: 2.5, md: 3 },
                                            backgroundColor: "#f9fafb",
                                            border: "1px solid #eef0f3",
                                            borderRadius: "18px",
                                            transition: "all 0.25s ease",
                                            "& .valor-icon": { transition: "all 0.25s ease" },
                                            "&:hover": {
                                                transform: "translateY(-4px)",
                                                backgroundColor: "#fff",
                                                borderColor: "#ffc845",
                                                boxShadow: "0 18px 40px rgba(11, 36, 61, 0.1)",
                                            },
                                            "&:hover .valor-icon": { backgroundColor: "#0b243d", color: "#ffc845" },
                                        }}
                                    >
                                        <Box
                                            className="valor-icon"
                                            sx={{
                                                width: 52,
                                                height: 52,
                                                borderRadius: "14px",
                                                backgroundColor: "rgba(255, 200, 69, 0.22)",
                                                color: "#0b243d",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <Icon sx={{ fontSize: 28 }} />
                                        </Box>
                                        <Box sx={{ mt: 2, fontSize: "1.15rem", fontWeight: 700, color: "#0b243d" }}>{valor.titulo}</Box>
                                        <Box sx={{ mt: 1, fontSize: "0.92rem", lineHeight: 1.6, color: "#4b5563" }}>{valor.texto}</Box>
                                    </Box>
                                </motion.div>
                            );
                        })}
                    </Box>
                </Box>
            </Box>

            {/* Como funciona */}
            {/* <HowItWorks /> */}

            {/* Chamada final */}
            <Box
                component="section"
                sx={{
                    position: "relative",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #0b243d 0%, #12365a 100%)",
                    color: "#fff",
                    py: { xs: 8, md: 10 },
                    overflow: "hidden",
                }}
            >
                <Box sx={anelDecorativo({ top: { xs: -100, md: -160 }, right: { xs: -100, md: -160 } })} />
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.6 }}
                    style={{ position: "relative", zIndex: 1, width: "90%", maxWidth: "760px", textAlign: "center" }}
                >
                    <h2 style={{ margin: "0 0 16px", fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)", fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.5px" }}>
                        Sua contabilidade em <span style={{ color: "#ffc845" }}>mãos especializadas.</span>
                    </h2>
                    <Box component="p" sx={{ m: "0 auto", maxWidth: "580px", fontSize: { xs: "1rem", md: "1.08rem" }, lineHeight: 1.7, color: "rgba(255, 255, 255, 0.8)" }}>
                        Você cuida das pessoas. A gente cuida de você. Vamos conversar?
                    </Box>
                    <Box sx={{ mt: 4, display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "center", gap: 1.5 }}>
                        <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => navigate("../Planos")} sx={primaryButtonSx}>
                            Conheça nossos planos
                        </Button>
                        <Button variant="outlined" startIcon={<WhatsAppIcon />} onClick={falarConosco} sx={outlinedDarkButtonSx}>
                            Fale com a gente
                        </Button>
                    </Box>
                    <Box sx={{ mt: 3, display: "flex", alignItems: "center", justifyContent: "center", gap: 1, fontSize: "0.9rem", color: "rgba(255, 255, 255, 0.75)" }}>
                        <CheckIcon sx={{ fontSize: 18, color: "#ffc845" }} />
                        Sem multa e sem fidelidade
                    </Box>
                </motion.div>
            </Box>

            <Footer />
        </>
    );
};

export default AboutUs;
